import { sanityOriginClient } from './client';
import type {
  ProductUpdateCategory,
  ProductUpdatePlatform,
  SanityProductUpdate,
  SanityProductUpdateSummary
} from './types';

/**
 * Product updates (FFH-3205) live under their own prefix on the main site
 * rather than a subdomain, so they share the header, sign-in and analytics.
 */
export const WHATS_NEW_PATH = '/whats-new';

export const buildProductUpdatePath = (slug: string): string =>
  `${WHATS_NEW_PATH}/${slug}`;

/**
 * Mirrors PRODUCT_UPDATE_CATEGORIES in sanity/lib/productUpdates.ts; a test
 * keeps the two in step.
 */
export const PRODUCT_UPDATE_CATEGORIES: readonly ProductUpdateCategory[] = [
  'newFeature',
  'improvement',
  'spotlight',
  'beta',
  'comingSoon'
];

export const BETA_CATEGORY: ProductUpdateCategory = 'beta';

/** Mirrors PRODUCT_UPDATE_PLATFORMS in sanity/lib/productUpdates.ts. */
export const PRODUCT_UPDATE_PLATFORMS: readonly ProductUpdatePlatform[] = [
  'app',
  'website',
  'both'
];

export const isProductUpdateCategory = (
  value: unknown
): value is ProductUpdateCategory =>
  (PRODUCT_UPDATE_CATEGORIES as readonly unknown[]).includes(value);

const IMAGE_ASSET_PROJECTION = `asset->{ _id, url, metadata { dimensions } }`;

const PRODUCT_UPDATE_SUMMARY_PROJECTION = `
    _id,
    title,
    "slug": slug.current,
    standfirst,
    category,
    platform,
    publishedAt,
    _createdAt,
    featuredImage { ..., ${IMAGE_ASSET_PROJECTION} },
    callToAction { label, url }
`;

const PRODUCT_UPDATE_PROJECTION = `${PRODUCT_UPDATE_SUMMARY_PROJECTION},
    lastUpdatedAt,
    body[]{
      ...,
      _type == "image" => { ..., ${IMAGE_ASSET_PROJECTION} }
    },
    seo {
      metaTitle,
      metaDescription,
      ogImage { ..., ${IMAGE_ASSET_PROJECTION} },
      canonicalUrl,
      isHiddenFromSearch
    }
`;

export const productUpdatesQuery = `
  *[_type == "productUpdate" && defined(slug.current) && defined(category)]
    | order(coalesce(publishedAt, _createdAt) desc) {${PRODUCT_UPDATE_SUMMARY_PROJECTION}
  }
`;

export const productUpdateBySlugQuery = `
  *[_type == "productUpdate" && slug.current == $slug][0] {${PRODUCT_UPDATE_PROJECTION}
  }
`;

export const allProductUpdateSlugsQuery = `
  *[_type == "productUpdate" && defined(slug.current)] { "slug": slug.current }
`;

export const getProductUpdates = async (): Promise<
  SanityProductUpdateSummary[]
> =>
  (
    await sanityOriginClient.fetch<SanityProductUpdateSummary[] | null>(
      productUpdatesQuery
    )
  )?.filter((update) => isProductUpdateCategory(update.category)) ?? [];

export const getProductUpdate = async (
  slug: string
): Promise<SanityProductUpdate | null> =>
  (await sanityOriginClient.fetch<SanityProductUpdate | null>(
    productUpdateBySlugQuery,
    { slug }
  )) ?? null;

/**
 * Reads the draft when one exists and the published update otherwise. Only
 * the preview route calls it.
 */
export const getProductUpdateDraft = async (
  slug: string
): Promise<SanityProductUpdate | null> =>
  (await sanityOriginClient
    .withConfig({ perspective: 'drafts' })
    .fetch<SanityProductUpdate | null>(productUpdateBySlugQuery, { slug })) ??
  null;
